"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Plus, Zap, Wind, Shield, Activity, X, ChevronRight } from "lucide-react";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const features = [{
  id: "upper",
  title: "Aero-Knit Upper",
  description: "Engineered for maximum breathability and adaptive fit. The single-piece construction eliminates friction points while providing targeted support zones.",
  x: "35%",
  y: "30%",
  icon: <Wind className="size-5" />,
  stats: [{
    label: "Weight",
    value: "45g"
  }, {
    label: "Airflow",
    value: "98%"
  }],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/anatomy_detail_upper.png"
}, {
  id: "midsole",
  title: "Carbon Shank Core",
  description: "Embedded carbon fiber plate delivers explosive energy return with every stride. Designed to propel you forward while maintaining torsional stability.",
  x: "55%",
  y: "65%",
  icon: <Activity className="size-5" />,
  stats: [{
    label: "Energy Return",
    value: "85%"
  }, {
    label: "Stiffness",
    value: "High"
  }],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/anatomy_detail_midsole.png"
}, {
  id: "heel",
  title: "LockDown Heel",
  description: "3D-molded heel counter ensures zero slippage. The memory foam lining adapts to your unique heel shape for a custom, secure feel.",
  x: "85%",
  y: "45%",
  icon: <Shield className="size-5" />,
  stats: [{
    label: "Stability",
    value: "10/10"
  }, {
    label: "Padding",
    value: "12mm"
  }],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/anatomy_detail_heel.png"
}, {
  id: "sole",
  title: "GripTech Outsole",
  description: "Multi-surface traction pattern inspired by automotive tire technology. Provides superior grip on wet and dry surfaces without compromising durability.",
  x: "25%",
  y: "85%",
  icon: <Zap className="size-5" />,
  stats: [{
    label: "Traction",
    value: "Wet/Dry"
  }, {
    label: "Durability",
    value: "500mi+"
  }],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/anatomy_detail_sole.png"
}];
export default function AnatomyOfAStride() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [openDrawer, setOpenDrawer] = useState<string | null>(null);
  return <section id="anatomy-of-a-stride" ref={ref} className={`dark bg-background text-foreground relative overflow-hidden py-24 transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-10">
        <div className="h-[800px] w-[800px] rounded-full bg-primary blur-[120px]" />
      </div>
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/grid_pattern.png)] bg-[size:40px_40px] opacity-[0.03]" />

      <div className="container relative mx-auto px-4">
        <div className="mb-16 text-center">
          <h2 className="text-primary mb-4 text-sm font-bold uppercase tracking-widest">
            Technical Specifications
          </h2>
          <h3 className="text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
            Anatomy of a Stride
          </h3>
          <p className="text-muted-foreground mx-auto mt-6 max-w-2xl text-lg">
            Explore the cutting-edge engineering behind our flagship performance
            runner. Tap the markers to reveal the technology.
          </p>
        </div>

        <div className="relative mx-auto aspect-[4/3] w-full max-w-5xl md:aspect-[16/9]">
          {}
          <div className="relative h-full w-full">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/anatomy_shoe_main.png" alt="StrideVault Performance Shoe Side Profile" className="h-full w-full object-contain drop-shadow-2xl" />

            {}
            {features.map((feature, i) => <SafeHydrate><Drawer key={feature.id} direction="right" open={openDrawer === feature.id} onOpenChange={open => setOpenDrawer(open ? feature.id : null)}>
                <DrawerTrigger asChild>
                  <div data-index={i} className="absolute -translate-x-1/2 -translate-y-1/2" style={{
                  left: feature.x,
                  top: feature.y
                }}>
                    <Button variant="outline" size="icon" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground h-10 w-10 rounded-full border-2 bg-background/80 shadow-[0_0_15px_rgba(var(--primary),0.5)] backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:shadow-[0_0_25px_rgba(var(--primary),0.8)]" aria-label={`View details for ${feature.title}`}>
                      <Plus className="h-6 w-6" />
                    </Button>
                    {}
                    <span className="border-primary absolute inset-0 -z-10 animate-ping rounded-full border opacity-75" />
                  </div>
                </DrawerTrigger>

                <DrawerContent className="dark bg-card border-l-border h-full max-w-md border-l">
                  <div className="flex h-full flex-col">
                    <DrawerHeader className="border-border border-b p-6 text-left">
                      <div className="flex items-center justify-between">
                        <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-full">
                          {feature.icon}
                        </div>
                        <DrawerClose asChild>
                          <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
                            <X className="h-5 w-5" />
                          </Button>
                        </DrawerClose>
                      </div>
                      <DrawerTitle className="mt-4 text-2xl font-bold">
                        {feature.title}
                      </DrawerTitle>
                      <DrawerDescription className="text-muted-foreground mt-2 text-base">
                        {feature.description}
                      </DrawerDescription>
                    </DrawerHeader>

                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="bg-muted/30 mb-6 overflow-hidden rounded-lg border border-white/5">
                        <img src={feature.image} alt={`${feature.title} close-up detail`} className="h-48 w-full object-cover" />
                      </div>

                      <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white/60">
                        Performance Data
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        {feature.stats.map((stat, idx) => <div key={idx} data-index={idx} className="bg-muted/20 rounded-lg p-4">
                            <div className="text-muted-foreground text-xs uppercase tracking-wide">
                              {stat.label}
                            </div>
                            <div className="text-primary mt-1 text-xl font-bold">
                              {stat.value}
                            </div>
                          </div>)}
                      </div>

                      <div className="bg-primary/5 border-primary/20 mt-8 rounded-lg border p-4">
                        <div className="flex items-start gap-3">
                          <div className="bg-primary/20 mt-1 rounded-full p-1">
                            <Zap className="text-primary h-3 w-3" />
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-white">
                              Pro Tip
                            </h5>
                            <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                              This feature is specifically designed to enhance
                              performance during high-intensity interval
                              training and long-distance runs.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <DrawerFooter className="border-border border-t p-6">
                      <Button className="w-full" asChild>
                        <Link to="/shoe-tech-size-guide">
                          View Full Specs <ChevronRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </DrawerFooter>
                  </div>
                </DrawerContent>
              </Drawer></SafeHydrate>)}
          </div>
        </div>

        {}
        <div className="mt-8 flex items-center justify-center gap-2 text-sm text-white/40 md:hidden">
          <Plus className="h-4 w-4" />
          <span>Tap markers to explore</span>
        </div>
      </div>
    </section>;
}